import { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

export default function CouponCard({ result, phone, onNewSpin }) {
  const [copied, setCopied] = useState(false);
  const { lang } = useApp();
  const tr = t[lang];
  const { offer, code, expiresAt } = result;
  const isNoLuck = offer.offer_type === "no_luck";

  useEffect(() => {
    if (!isNoLuck) {
      const duration = 3000;
      const end = Date.now() + duration;
      const colors = ["#1E5FA8", "#2B8FD4", "#F5A623", "#27AE60", "#E74C3C"];
      const frame = () => {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const locale = lang === "ar" ? "ar-SA" : "en-US";
  const expiryFormatted = new Date(expiresAt).toLocaleDateString(locale);

  // Use English fields if available and lang is en
  const offerTitle = (lang === "en" && offer.title_en) ? offer.title_en : offer.title;
  const offerDesc = (lang === "en" && offer.description_en) ? offer.description_en : offer.description;
  const offerTerms = (lang === "en" && offer.terms_en) ? offer.terms_en : offer.terms;

  const whatsappMsg = lang === "ar"
    ? `مرحباً! لقد فزت بعرض "${offerTitle}" من مغاسل القمر الأبيض 🌙\n\nكود الكوبون: *${code}*\nصالح حتى: ${expiryFormatted}\n\n${offerTerms || ""}`
    : `Hello! I won the "${offerTitle}" offer from White Moon Laundry 🌙\n\nCoupon Code: *${code}*\nValid Until: ${expiryFormatted}\n\n${offerTerms || ""}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMsg)}`;

  if (isNoLuck) {
    return (
      <div className="flex flex-col items-center gap-6 prize-reveal">
        <div className="text-center">
          <div className="text-6xl mb-4">🍀</div>
          <h2 className="text-2xl font-black text-muted-foreground">{tr.betterLuck}</h2>
          <p className="text-muted-foreground mt-2">
            {lang === "ar" ? "شكراً لمشاركتك. نتمنى لك حظاً أوفر في المرة القادمة!" : "Thank you for participating. Better luck next time!"}
          </p>
        </div>
        <div className="bg-card rounded-2xl p-6 shadow-lg border border-border w-full max-w-sm text-center">
          <p className="text-sm text-muted-foreground">
            {lang === "ar" ? "لا تيأس! قد تحمل لك زيارتك القادمة عرضاً رائعاً 🌟" : "Don't give up! Your next visit might bring an amazing offer 🌟"}
          </p>
          <p className="text-xs text-muted-foreground mt-2">{lang === "ar" ? "رقم جوالك:" : "Your phone:"} {phone}</p>
        </div>
        <button onClick={onNewSpin} className="text-sm text-primary underline">
          {lang === "ar" ? "العودة للصفحة الرئيسية" : "Back to Home"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 prize-reveal">
      {/* Win Header */}
      <div className="text-center">
        <div className="text-5xl mb-3">🎉</div>
        <h2 className="text-3xl font-black text-primary">{tr.congratulations}</h2>
        <p className="text-muted-foreground mt-1">
          {lang === "ar" ? "احتفظ بهذا الكوبون واستخدمه في أقرب وقت" : "Keep this coupon and use it as soon as possible"}
        </p>
      </div>

      {/* Coupon Card */}
      <div className="w-full max-w-sm">
        <div className="relative bg-gradient-to-br from-primary to-accent text-white rounded-3xl p-1 shadow-2xl">
          <div className="bg-card rounded-2xl p-6 text-foreground">
            {/* Offer Name */}
            <div className="text-center mb-4">
              <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full mb-2">
                🎁 {lang === "ar" ? "عرضك الحصري" : "Your Exclusive Offer"}
              </span>
              <h3 className="text-xl font-black text-primary">{offerTitle}</h3>
              {offerDesc && <p className="text-sm text-muted-foreground mt-1">{offerDesc}</p>}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-2 my-4">
              <div className="flex-1 border-t-2 border-dashed border-border"></div>
              <span className="text-muted-foreground">✂</span>
              <div className="flex-1 border-t-2 border-dashed border-border"></div>
            </div>

            {/* Coupon Code */}
            <div className="bg-secondary/50 rounded-xl p-4 text-center mb-4">
              <p className="text-xs text-muted-foreground mb-1">{tr.couponCode}</p>
              <p className="text-2xl font-black tracking-widest text-primary font-mono">{code}</p>
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{tr.expiresAt}:</span>
                <span className="font-bold">{expiryFormatted}</span>
              </div>
              {offer.min_invoice_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{lang === "ar" ? "الحد الأدنى للفاتورة:" : "Min. Invoice:"}</span>
                  <span className="font-bold">{offer.min_invoice_amount} {lang === "ar" ? "ر.س" : "SAR"}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{lang === "ar" ? "رقم الجوال:" : "Phone:"}</span>
                <span className="font-bold" dir="ltr">{phone}</span>
              </div>
            </div>

            {/* Terms */}
            {offerTerms && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg">
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  <strong>📋 {tr.terms}:</strong> {offerTerms}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Decorative dots */}
        <div className="flex justify-between px-6 -mt-1">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-3 h-3 rounded-full bg-background border border-border"></div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        <button
          onClick={handleCopy}
          className="w-full py-3.5 bg-primary text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors shadow-md"
        >
          {copied ? `✅ ${tr.copied}` : `📋 ${tr.copyCode}`}
        </button>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3.5 bg-green-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-green-600 transition-colors shadow-md text-center"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          {tr.shareWhatsapp}
        </a>
      </div>

      <button onClick={onNewSpin} className="text-sm text-muted-foreground underline mt-2">
        {lang === "ar" ? "العودة للصفحة الرئيسية" : "Back to Home"}
      </button>
    </div>
  );
}